import React from 'react'
import { formatCurrency, IsSpecialEmployer } from 'application/helpers'
import { Container, Row, Col, Card } from 'react-bootstrap'
import { AccordionSection, AccordionCard } from '../../accordion'
import ContactUsDialog from '../../dialog/contactUsDialog'
import Tooltip from '../../tooltip'
import * as E from './financialInclusionSectionElements'
import * as S from './estimateDetailElements'
import { EstimateDetailStaticData } from './estimateDetailStaticData'
import { PrimaryLink } from 'application/components/styledComponents/link'
import { LeaseInsuranceInfoBlock } from 'application/components/styledComponents/LeaseInsuranceInfoBlock/LeaseInsuranceInfoBlock'
import zenscroll from 'zenscroll'

const FinancialInclusionSection = ({
  data: {
    financeAmount,
    compInsurance,
    priveawayPrice,
    compInsurancePremium,
    leaseProtectionPremium,
    rpcPremium,
    rpcProvider,
    extendedWarrantyPremium,
    gapCoverPremium,
    annualRate,
    leaseTermYears,
    extendedWarrantyProvider,
    brand,
    consultantFirstName,
    userFirstName,
    stampDutyViaDisbursement,
    financierEstablishmentFee,
    negativeEquityAmount,
    displayInterest,
    extendedWarrantyTerm,
    registrationFee,
    ctp,
    stampDuty,
    compInsurancePremiumFinanced,
    compInsuranceProvider,
  },
  applicationRole,
  expandAccordion,
  theme,
}) => {
  const scrollTo = (id) => {
    var about = document.getElementById(id)
    zenscroll.to(about)
  }

  const getAddOnTerm = () => {
    return extendedWarrantyTerm && extendedWarrantyTerm > 0
      ? extendedWarrantyTerm > 1
        ? '- ' + extendedWarrantyTerm + ' years'
        : '- ' + extendedWarrantyTerm + ' year'
      : ''
  }
  
  let hasFirstYearComprehensiveInsurance = false
  let financialInclusionAccordionTitle =
    compInsurancePremiumFinanced && compInsurancePremium > 0
      ? 'Includes the car, first year comprehensive insurance and add-on insurances to protect the lease itself.'
      : 'Includes the car, add-on insurances to protect the lease itself.'
  const pacVal = 'pac'
  const rpcProviderlower = rpcProvider && rpcProvider.toLowerCase()
  const extendedWarrantyProviderlower =
    extendedWarrantyProvider && extendedWarrantyProvider.toLowerCase()
  const isPacInsurer =
    rpcProviderlower === pacVal || extendedWarrantyProviderlower === pacVal
  const leaseProtectionInsurance = 'Lease Protection Insurance'
  const lpaTooltip = isPacInsurer
    ? 'When life throws up those big challenges, like losing your job unexpectedly, Lease Protection Insurance is there to assist with not only covering your lease payments, but the costs associated with running your car as well. Life can be unpredictable, so it’s nice to know that in the event of an involuntary redundancy, your novated lease is one thing you won’t need to worry about.'
    : 'Cover your lease repayments if you become involuntarily unemployed.'
  const totalLossAssist = isPacInsurer
    ? 'Total Loss Assist Insurance'
    : 'Total Loss Assist'
  const tLatooltip = isPacInsurer
    ? 'Financial assistance if your vehicle is deemed a total loss by your comprehensive insurer. Total Loss Assist covers the difference between the comprehensive insurance payout and the greater of either the original price, or the amount needed to discharge the lease. Providing you with greater protection in the event your vehicle is written off.'
    : 'Cover in addition to your Comprehensive Motor Vehicle insurance payout if your car is written off. Covers the difference between your comprehensive insurer’s payout amount and either the amount you originally paid for the vehicle or the amount needed to settle your finance contract. The minimum payout for any claim is $5000.'
  const safeGuardorExtended = isPacInsurer
    ? `Extended Warranty Insurance  ${getAddOnTerm()}`
    : 'Safeguard Ultimate Warranty'
  const extendedTooltip = isPacInsurer
    ? 'The Extended Warranty is designed to reduce the financial impact of unexpected mechanical or electrical failure by providing cover for most components and parts as covered by the original manufacturer’s warranty. Drive with confidence knowing you’re prepared for almost anything that the road could throw at you.'
    : 'Protect yourself by extending your Manufacturer’s Warranty. This covers parts and labour on components when you hit unexpected mechanical or electrical failure.'
  const extendedOneLiner = isPacInsurer
    ? 'Cover for unexpected mechanical or electrical failure to most components.'
    : `Extend the car’s factory warranty.`
  const rpcText = isPacInsurer
    ? 'Guaranteed Buy Back Insurance'
    : 'Residual Protection Cover'
  const rpcTooltip = isPacInsurer
    ? 'Once your novated lease has reached full term you may decide to sell your vehicle to pay the residual amount owed to the financier. If however the market value of your car is less than the residual amount, there may be a gap payment that you still need to make – GBB provides protection in this scenario and in most circumstances, covers this gap payment for you.'
    : 'Cover in case the trade-in value of your car is less than the ATO discounted value. Provides protection for the difference between the trade-in value of your vehicle and the ATO calculated value at the end of your lease term'
  const rpcOneLiner = isPacInsurer
    ? 'Cover in most scenarios if the market value of your car is less than the residual amount at the end of your full lease term.'
    : 'Cover in case the car’s market value is less than the residual.'
  const learnMoreURL =
    `https://www.${global.config.brand.toLowerCase()}.com.au/insurance/` +
    (isPacInsurer ? 'novated-lease' : 'insurance-policy-information')
  const isNSW = IsSpecialEmployer('NSWHN')
  const isSAGBYO = IsSpecialEmployer('SAGBYO')

  const InitFinancialInclusionAccordionTitle = (compInsurance, isSAGBYO) => {
    if (
      compInsurance &&
      compInsurance.length > 0 &&
      compInsurance.toString().toLowerCase() !== 'no' &&
      compInsurancePremiumFinanced
    ) {
      hasFirstYearComprehensiveInsurance = true
      financialInclusionAccordionTitle =
        'Includes the car, first year comprehensive insurance and add-on insurances to protect the lease itself.'
    } else {
      hasFirstYearComprehensiveInsurance = false
      financialInclusionAccordionTitle = isSAGBYO ? '': 'Includes the car and add-on insurances to protect the lease itself.'
    }
  }

  InitFinancialInclusionAccordionTitle(compInsurance, isSAGBYO)

  return (
    <Container className="section mt-5 mb-5 financialInclusion print-page-break-before">
      {financeAmount > 0 && (
        <div>
          <S.StyledH3 className="mb-4">
            Let's talk finance and add-on insurance inclusions
          </S.StyledH3>
          <S.GreyTextDiv>
            {"Here's where we dig a little deeper into what your estimated amount financed includes." + (isSAGBYO ? '' : " Like the car and add-on lease insurances.")}
          </S.GreyTextDiv>

          <Card className="mb-13rem mt-5 card-shadow">
            <Card.Header className="border-bottom-0 d-flex justify-content-between">
              <S.CardHeader xs md={9} className="px-0">
                Your total amount financed
              </S.CardHeader>
              <S.CardHeaderValue id="financialinclusionsection.financeamount1">
                {formatCurrency(financeAmount)}
              </S.CardHeaderValue>
            </Card.Header>
            <Card.Body>
              <AccordionSection
                readerText={'your total amount financed'}
                expandAccordion={expandAccordion}
                title={financialInclusionAccordionTitle}
                id="financialinclusionsection.accordion"
              >
                <div className="mb-3">
                  <Row>
                    <S.SubHeadingDiv className="ml-3">
                      Car finance
                    </S.SubHeadingDiv>
                  </Row>
                  {priveawayPrice > 0 && (
                    <Row>
                      <E.LineTitles lg={9} xs={7}>
                        {brand} drive away price
                        <br />
                        <E.SmallerSubText>
                          {EstimateDetailStaticData('IncludesTheCar')}
                        </E.SmallerSubText>
                      </E.LineTitles>
                      <S.CardValue
                        className="text-right"
                        id="financialinclusionsection.driveawayprice"
                      >
                        {formatCurrency(priveawayPrice)}
                      </S.CardValue>
                    </Row>
                  )}

                  {(isNSW  || isSAGBYO) && (registrationFee + ctp + stampDuty > 0) && (
                    <E.feeDetail theme={theme}>
                      <Row>
                        <E.LineTitles>
                          '{brand} drive away price' includes the below on-road
                          charges:
                        </E.LineTitles>
                      </Row>
                      <Row>
                        <E.LineTitles>Registration</E.LineTitles>
                        <S.CardValue id="financialinclusionsection.registrationfee">
                          {formatCurrency(registrationFee)}
                        </S.CardValue>
                      </Row>
                      <Row>
                        <E.LineTitles>Compulsary Third Party</E.LineTitles>
                        <S.CardValue id="financialinclusionsection.ctp">
                          {formatCurrency(ctp)}
                        </S.CardValue>
                      </Row>
                      <Row>
                        <E.LineTitles>Stamp Duty</E.LineTitles>
                        <S.CardValue id="financialinclusionsection.stampduty">
                          {formatCurrency(stampDuty)}
                        </S.CardValue>
                      </Row>
                    </E.feeDetail>
                  )}

                  {stampDutyViaDisbursement > 0 && (
                    <Row>
                      <E.LineTitles lg={9} xs={7}>
                        <div className="d-flex">
                          <span className="mr-2">
                            Stamp duty via disbursement{' '}
                          </span>
                          <Tooltip
                            header="Stamp duty via disbursement"
                            text="Stamp duty is a tax for transferring registration of the car to you, levied by state governments. In Stamp Duty via disbursement, you can include it in your total amount financed. In which case, your financier pays you the Stamp Duty, which you then pass
 onto the regular authority (like an interstate dealer or private sale). "
                            id="financialinclusionsection.stampdutytooltip"
                          />
                        </div>
                      </E.LineTitles>
                      <S.CardValue id="financialinclusionsection.stampduty">
                        {formatCurrency(stampDutyViaDisbursement)}
                      </S.CardValue>
                    </Row>
                  )}

                  {negativeEquityAmount > 0 && (
                    <Row>
                      <Col>
                        <div className="d-flex ">
                          <span className="mr-2">Negative Equity </span>
                          <Tooltip
                            header="Negative Equity"
                            text="Negative equity applies if the car you’re currently driving is worth less than the payout amount. E.g. You trade in the car for $13,500, but the payout on the lease is $15,000, leaving a gap of $1,500. You can roll this gap amount into the finance on your
 new lease if the financier approves."
                            id="financialinclusionsection.negativeequitytooltip"
                          />
                        </div>
                      </Col>
                      <S.CardValue id="financialinclusionsection.negativeequity">
                        {formatCurrency(negativeEquityAmount)}
                      </S.CardValue>
                    </Row>
                  )}

                  {financierEstablishmentFee > 0 && (
                    <Row>
                      <E.LineTitles>
                        <div className="d-flex ">
                          <span className="mr-2">
                            Financier's establishment fee{' '}
                          </span>
                          <Tooltip
                            header="Financier's establishment fee"
                            text="A one-time fee for setting up your lease with the financier."
                            id="financialinclusionsection.financierfeetooltip"
                          />
                        </div>
                      </E.LineTitles>
                      <S.CardValue id="financialinclusionsection.financierfee">
                        {formatCurrency(financierEstablishmentFee)}
                      </S.CardValue>
                    </Row>
                  )}

                  {compInsurancePremium > 0 && compInsurancePremiumFinanced && (
                    <Row>
                      <E.LineTitles lg={9} xs={7} id="renewal_link">
                        1st year Comprehensive Insurance ({brand} arranged)
                        {global.config.isBrandMX ? (
                          '^'
                        ) : (
                          <PrimaryLink
                            onClick={() => scrollTo('renewal_description')}
                            className="text-dark"
                          >
                            ^
                          </PrimaryLink>
                        )}
                        <br />
                        <E.SmallerSubText>
                          {compInsuranceProvider === 'Allianz' && compInsurancePremiumFinanced && compInsurancePremium > 0
                            ? 'You have the choice of repairer. Please refer to your PDS for details about choice of repairer.'
                            : ''}
                          Chat to your consultant if you’d like to arrange this
                          yourself.
                        </E.SmallerSubText>
                      </E.LineTitles>
                      <S.CardValue id="financialinclusionsection.compinsurancepremium">
                        {formatCurrency(compInsurancePremium)}
                      </S.CardValue>
                    </Row>
                  )}
                </div>

                {!isSAGBYO && <>
                  <div className="mb-3">
                    {(rpcPremium > 0 ||
                      leaseProtectionPremium > 0 ||
                      extendedWarrantyPremium > 0 ||
                      gapCoverPremium > 0) && (
                      <div>
                        <Row>
                          <Col className="text-header">
                            <S.SubHeadingDiv className="d-flex">
                              <span className="mr-2">
                                Add-on lease insurances (currently included){' '}
                              </span>
                              <Tooltip
                                header="Add-on lease insurances you’ve chosen"
                                text="The Motor Vehicle Lease Insurance Options Policy includes four different cover options that you may be eligible for and from which you can choose. The products are add-on however can’t be added once your lease has begun. Further information has been provided below on each of the covers to help you make an informed decision. You should read the product disclosure statements before making a decision about these products."
                                id="financialinclusionsection.addoninsurancetooltip"
                              />
                            </S.SubHeadingDiv>
                          </Col>
                        </Row>

                        {leaseProtectionPremium > 0 && (
                          <LeaseInsuranceInfoBlock
                            Included
                            Title={leaseProtectionInsurance}
                            TitleId="financialinclusionsection.leaseprotectionpremiumtitle1"
                            TooltipText={lpaTooltip}
                            TooltipId="financialinclusionsection.leaseprotectiontooltip1"
                            Subtext="Cover your lease repayments if you become involuntarily unemployed."
                            MoreInfoUrl={learnMoreURL + '#lpi'}
                            PremiumAmount={leaseProtectionPremium}
                            PremiumId="financialinclusionsection.leaseprotectionpremium"
                          />
                        )}

                        {rpcPremium > 0 && (
                          <LeaseInsuranceInfoBlock
                            Included
                            Title={rpcText}
                            TitleId="financialinclusionsection.rpctitle1"
                            TooltipText={rpcTooltip}
                            TooltipId="financialinclusionsection.rpctooltip1"
                            Subtext={rpcOneLiner}
                            MoreInfoUrl={learnMoreURL + '#gbbi'}
                            PremiumAmount={rpcPremium}
                            PremiumId="financialinclusionsection.rpcpremium"
                          />
                        )}

                        {gapCoverPremium > 0 && (
                          <LeaseInsuranceInfoBlock
                            Included
                            Title={totalLossAssist}
                            TitleId="financialinclusionsection.totallosstitle1"
                            TooltipText={tLatooltip}
                            TooltipId="financialinclusionsection.totallossassisttooltip1"
                            Subtext="Cover in addition to your Comprehensive MV insurance payout if your car is written off."
                            MoreInfoUrl={learnMoreURL + '#tla'}
                            PremiumAmount={gapCoverPremium}
                            PremiumId="financialinclusionsection.totallosspremium"
                          />
                        )}

                        {extendedWarrantyPremium > 0 && (
                          <LeaseInsuranceInfoBlock
                            Included
                            Title={safeGuardorExtended}
                            TitleId="financialinclusionsection.extendedwarrantytitle1"
                            TooltipText={extendedTooltip}
                            TooltipId="financialinclusionsection.extendedwarrantytooltip1"
                            Subtext={extendedOneLiner}
                            MoreInfoUrl={learnMoreURL + '#ew'}
                            PremiumAmount={extendedWarrantyPremium}
                            PremiumId="financialinclusionsection.extendedwarrantypremium"
                          />
                        )}
                      </div>
                    )}
                    <Row className="text-right font-weight-bold border-top mx-0 pt-2 mt-3">
                      <S.CardText bold xs md={9} className="px-0">
                        Total amount financed
                      </S.CardText>
                      <S.CardValue
                        bold
                        xs
                        md={3}
                        className="px-0"
                        id="financialinclusionsection.financeamount2"
                      >
                        {formatCurrency(financeAmount)}
                      </S.CardValue>
                    </Row>
                  </div>

                  <div className="mb-3">
                    <AccordionCard
                      title={
                        global.config.isBrandMX
                          ? 'Do I have to include insurances on my lease?'
                          : 'Do I have to include lease insurances?'
                      }
                      expandAccordion={expandAccordion}
                      id="financialinclusionsection.includeinsurancesaccordion"
                      smallerTitleText={global.config.isBrandRS}
                    >
                      <S.BrandDefaultText>
                        Comprehensive insurance is the only one you’ll need to
                        include. Though many customers find the add-on insurances
                        useful when unexpected things pop up. Like mechanical
                        faults after the factory warranty expires.
                      </S.BrandDefaultText>
                    </AccordionCard>
                  </div>

                  <div className="mb-3">
                    {hasFirstYearComprehensiveInsurance && (
                      <S.BrandDefaultText>
                        <ContactUsDialog
                          className="link font-weight-bold text-underline text-dark"
                          theme="link"
                          launchText="Contact your consultant"
                          id="financialinclusionsection.contactus"
                        />{' '}
                        if you'd like to arrange your comprehensive insurance
                        yourself. Want to make changes to your selection of lease
                        insurances? {consultantFirstName} can help.
                      </S.BrandDefaultText>
                    )}
                  </div>
                </>}
                {displayInterest && (
                  <div
                    className="mb-3"
                    id="financialinclusionsection.interestrate"
                  >
                    Remember, you'll pay this amount back with {annualRate}%
                    interest.
                  </div>
                )}
                {!isSAGBYO && <>
                  <div className="mb-3">
                    {!(
                      rpcPremium > 0 &&
                      leaseProtectionPremium > 0 &&
                      extendedWarrantyPremium > 0 &&
                      gapCoverPremium > 0
                    ) && (
                      <>
                        <div className="text-brand font-weight-bold mb-2">
                          Add-on lease insurances (currently excluded)
                        </div>
                        <div id="financialinclusionsection.reviewsection">
                          You'll want to take the time to review this section{' '}
                          {userFirstName}.
                        </div>
                      </>
                    )}
                    {(rpcPremium === 0 ||
                      leaseProtectionPremium === 0 ||
                      extendedWarrantyPremium === 0 ||
                      gapCoverPremium === 0) && (
                      <div>
                        <div>
                          Based on your conversation with your consultant, either
                          you’ve opted out of, or are ineligible for, the
                          following insurances:
                        </div>

                        {gapCoverPremium === 0 && (
                          <LeaseInsuranceInfoBlock
                            Title={totalLossAssist}
                            TitleId="financialinclusionsection.totallosstitle2"
                            TooltipText={tLatooltip}
                            TooltipId="financialinclusionsection.totallossassisttooltip2"
                            Subtext="Cover in addition to your Comprehensive MV insurance payout if your car is written off."
                            MoreInfoUrl={learnMoreURL + '#tla'}
                          />
                        )}

                        {rpcPremium === 0 && (
                          <LeaseInsuranceInfoBlock
                            Title={rpcText}
                            TitleId="financialinclusionsection.rpctitle2"
                            TooltipText={rpcTooltip}
                            TooltipId="financialinclusionsection.rpctooltip2"
                            Subtext={rpcOneLiner}
                            MoreInfoUrl={learnMoreURL + '#gbbi'}
                          />
                        )}

                        {leaseProtectionPremium === 0 && (
                          <LeaseInsuranceInfoBlock
                            Title={leaseProtectionInsurance}
                            TitleId="financialinclusionsection.leaseprotectionpremiumtitle2"
                            TooltipText={lpaTooltip}
                            TooltipId="financialinclusionsection.leaseprotectiontooltip2"
                            Subtext="Cover your lease repayments if you become involuntarily unemployed."
                            MoreInfoUrl={learnMoreURL + '#lpi'}
                          />
                        )}
                      </div>
                    )}
                    <S.GreyTextDiv className="mb-3 ml-0">
                      <hr></hr>
                      Understanding the{' '}
                      <a
                        href={learnMoreURL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={
                          global.config.isBrandMX
                            ? 'font-weight-bold'
                            : 'text-dark'
                        }
                        id="financialinclusionsection.learnmore9"
                      >
                        full benefits and conditions
                      </a>{' '}
                      of the insurances you choose to include or exclude with your
                      lease is important. Then choose which insurance options are
                      best suited to your circumstances as you won't be able to
                      take up additional insurance options once your lease has
                      begun.
                    </S.GreyTextDiv>

                    {compInsurancePremium > 0  && compInsurancePremiumFinanced && (
                      <div className="mb-3 text-small">
                        {global.config.isBrandMX ? (
                          '^'
                        ) : (
                          <PrimaryLink
                            id="renewal_description"
                            onClick={() => scrollTo('renewal_link')}
                            className="text-dark"
                          >
                            ^
                          </PrimaryLink>
                        )}
                        <E.AnnualRenewalFootnote>
                          {' '}
                          Annual renewals are covered by your running costs.
                        </E.AnnualRenewalFootnote>
                      </div>
                    )}

                    <E.DisclaimerDiv>
                      Disclaimer: please read the{' '}
                      <a
                        href={learnMoreURL + '#pds'}
                        target="_blank"
                        rel="noopener noreferrer"
                        id="financialinclusionsection.learnmore10"
                        className={
                          global.config.isBrandRS && 'text-dark font-weight-bold'
                        }
                      >
                        product disclosure statements
                      </a>{' '}
                      before making a decision about the insurance products.
                    </E.DisclaimerDiv>
                  </div>
                  <br />
                  <div className="print-page-break-before">
                    <AccordionCard
                      title="Why would I want insurances on my lease?"
                      expandAccordion={expandAccordion}
                      id="financialinclusionsection.whywantinsaccordion"
                      smallerTitleText={global.config.isBrandRS}
                    >
                      <S.BrandDefaultText id="financialinclusionsection.whywantinsaccordiontext">
                        {EstimateDetailStaticData('WhyWantInsurances', {
                          leaseTermYears: leaseTermYears,
                          firstName: userFirstName,
                        })}
                      </S.BrandDefaultText>
                    </AccordionCard>
                  </div>
                </>}
              </AccordionSection>
            </Card.Body>
          </Card>
        </div>
      )}
    </Container>
  )
}

export default FinancialInclusionSection
